import { useState } from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { PanelShell, StatGrid, StatTile, PanelUnavailable, FindingList } from "./PanelShell";

const AXIS_ORDER = ["roll", "pitch", "yaw"];
const TERM_COLOURS = { P: "#E7C25B", I: "#3a8eff", D: "#FF7A2F", FF: "#4ADE80" };

/**
 * Two views of the same loop: how it answers a step, and where it amplifies.
 * Coherence is drawn beside the gain because a gain peak at a frequency the
 * aircraft never flew is an artefact, not a resonance.
 */
const PIDPanel = ({ module }) => {
  const theme = useTheme();
  const axes = module?.panel?.axes || {};
  const available = AXIS_ORDER.filter((a) => axes[a]);
  const [axis, setAxis] = useState(available[0] || "roll");

  if (available.length === 0) {
    return <PanelUnavailable reason={module?.skip_reason || "No PID controller logging in this log."} />;
  }

  const panel = axes[axis] || axes[available[0]];
  const step = panel.step_time_s.map((t, i) => ({ t, y: panel.step_response[i] }));
  const spectrum = panel.frequencies.map((f, i) => ({
    freq: f,
    gain: panel.gain_db[i],
    coherence: panel.coherence[i],
  }));
  const terms = Object.entries(panel.term_rms).map(([term, rms]) => ({ term, rms }));

  return (
    <PanelShell
      title="PID Tracking"
      subtitle={`Closed-loop response estimated from flight data at ${panel.sample_rate_hz} Hz`}
      health={module.health}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={axis}
        onChange={(e, value) => value && setAxis(value)}
        sx={{ mb: 2 }}
      >
        {available.map((a) => (
          <ToggleButton key={a} value={a} sx={{ textTransform: "capitalize", px: 2 }}>
            {a}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <StatGrid>
        <StatTile label="Bandwidth" value={panel.bandwidth_hz} unit="Hz" tone={panel.bandwidth_hz >= 4 ? "good" : "warn"} />
        <StatTile label="Peak gain" value={`${panel.peak_gain}×`} tone={panel.peak_gain >= 1.3 ? "warn" : "good"} />
        <StatTile label="Peak at" value={panel.peak_gain_hz} unit="Hz" />
        {panel.overshoot_pct != null && (
          <StatTile
            label="Step overshoot"
            value={panel.overshoot_pct}
            unit="%"
            tone={panel.overshoot_pct > 25 ? "warn" : "good"}
          />
        )}
      </StatGrid>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: "uppercase" }}>
            Estimated step response
          </Typography>
          <Box sx={{ height: 220, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={step} margin={{ top: 8, right: 8, left: -18, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="t"
                  type="number"
                  domain={[0, "dataMax"]}
                  tickFormatter={(v) => `${v.toFixed(2)}s`}
                  tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                />
                <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
                <Tooltip
                  contentStyle={{ background: "#171717", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11 }}
                  labelFormatter={(l) => `${Number(l).toFixed(3)} s`}
                  formatter={(v) => [Number(v).toFixed(3), "response"]}
                />
                <ReferenceLine y={1} stroke="rgba(255,255,255,0.25)" strokeDasharray="4 4" />
                <Line type="monotone" dataKey="y" stroke="#E7C25B" strokeWidth={1.8} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        <Box>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: "uppercase" }}>
            Closed-loop gain &amp; coherence
          </Typography>
          <Box sx={{ height: 220, mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={spectrum} margin={{ top: 8, right: 8, left: -18, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis
                  dataKey="freq"
                  type="number"
                  domain={[0, 60]}
                  tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                  label={{ value: "Hz", position: "insideBottomRight", offset: -2, fontSize: 10 }}
                />
                <YAxis yAxisId="gain" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
                <YAxis yAxisId="coh" orientation="right" domain={[0, 1]} hide />
                <Tooltip
                  contentStyle={{ background: "#171717", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11 }}
                  labelFormatter={(l) => `${Number(l).toFixed(1)} Hz`}
                />
                <ReferenceLine yAxisId="gain" y={0} stroke="rgba(255,255,255,0.2)" />
                <Line yAxisId="gain" type="monotone" dataKey="gain" name="gain (dB)" stroke="#E7C25B"
                      strokeWidth={1.6} dot={false} isAnimationActive={false} />
                <Line yAxisId="coh" type="monotone" dataKey="coherence" name="coherence" stroke="rgba(58,142,255,0.6)"
                      strokeWidth={1} strokeDasharray="3 3" dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: "uppercase" }}>
          Where the output comes from (RMS per term)
        </Typography>
        <Box sx={{ height: 130, mt: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={terms} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="term" tick={{ fontSize: 11, fill: theme.palette.text.primary }} />
              <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
              <Tooltip
                contentStyle={{ background: "#171717", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11 }}
                formatter={(v) => [Number(v).toFixed(4), "RMS"]}
              />
              <Bar dataKey="rms" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                {terms.map((t) => (
                  <Cell key={t.term} fill={TERM_COLOURS[t.term] || "#888"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: theme.palette.text.secondary }}>
        The step response is reconstructed from ordinary flight, not a commanded step test — where coherence is
        low the aircraft was not excited at that frequency and the gain there means nothing. A gain peak above
        0 dB with high coherence is the loop amplifying its own commands: the frequency it will ring at.
      </Typography>

      <FindingList findings={module.findings} />
    </PanelShell>
  );
};

export default PIDPanel;
